/**
 * Il cruscotto dello scadenziario formativo.
 *
 * Un elenco di scadenze dice cosa succede ma non quando arriva addosso: a
 * leggere 71 righe in ordine di data non si capisce se il problema è questo
 * mese o fra sei, né su quale corso si concentra. Qui le stesse righe si
 * guardano da tre lati — nel tempo, per corso, per persona — e ogni elemento
 * è un filtro: si clicca una barra e sotto resta solo quello.
 */
import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';

const MESI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

// Rosso per ciò che è già scaduto, ambra per l'imminente, verde per il resto:
// sono i tre stati che contano, e il colore li dice prima del numero.
const ROSSO = '#dc2626', AMBRA = '#d97706', VERDE = '#2e854c', GRIGIO = '#cbd5e1';
const coloreGiorni = (g) => g < 0 ? ROSSO : g <= 30 ? AMBRA : g <= 90 ? '#eab308' : VERDE;

export default function CruscottoFormazione({ righe, filtri, onFiltro, onFiltri, onPersona }) {
  // Dodici mesi avanti più tutto l'arretrato in una colonna sola: lo scaduto
  // non ha un "quando", ha un "da quanto", e metterlo in scala con il futuro
  // schiaccerebbe tutto il resto.
  const perMese = useMemo(() => {
    const ora = new Date();
    const chiave = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const mesi = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(ora.getFullYear(), ora.getMonth() + i, 1);
      mesi.push({ k: chiave(d), etichetta: `${MESI[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`, n: 0, scaduti: 0 });
    }
    const arretrato = { k: 'scaduti', etichetta: 'scaduti', n: 0, scaduti: 1 };
    for (const r of righe) {
      if (r.giorni < 0) { arretrato.n++; continue; }
      const m = mesi.find(x => x.k === String(r.scadenza).slice(0, 7));
      if (m) m.n++;
    }
    return [...(arretrato.n ? [arretrato] : []), ...mesi];
  }, [righe]);

  // Per corso: dove si concentra il problema. Ordinati per urgenza, non per
  // nome: in cima quello che chiede attenzione.
  const perCorso = useMemo(() => {
    const m = new Map();
    for (const r of righe) {
      const c = m.get(r.corso) || { corso: r.corso, scaduti: 0, entro90: 0, ok: 0, tot: 0 };
      if (r.giorni < 0) c.scaduti++;
      else if (r.giorni <= 90) c.entro90++;
      else c.ok++;
      c.tot++;
      m.set(r.corso, c);
    }
    return [...m.values()].sort((a, b) =>
      (b.scaduti - a.scaduti) || (b.entro90 - a.entro90) || (b.tot - a.tot)).slice(0, 12);
  }, [righe]);

  // Per persona: chi ha più pendenze. Serve a sapere chi convocare.
  const perPersona = useMemo(() => {
    const m = new Map();
    for (const r of righe) {
      if (r.giorni > 90) continue;
      const p = m.get(r.persona) || { persona: r.persona, scaduti: 0, imminenti: 0 };
      if (r.giorni < 0) p.scaduti++; else p.imminenti++;
      m.set(r.persona, p);
    }
    return [...m.values()]
      .sort((a, b) => (b.scaduti - a.scaduti) || (b.imminenti - a.imminenti))
      .slice(0, 8);
  }, [righe]);

  const attivo = (tipo, valore) => filtri[tipo] === valore;
  const commuta = (tipo, valore) => onFiltro(tipo, attivo(tipo, valore) ? null : valore);
  const maxCorso = Math.max(1, ...perCorso.map(c => c.tot));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {/* Quando arriva addosso */}
      <div className="card lg:col-span-2">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Quando scadono</h3>
          <span className="text-xs text-gray-400">clicca una colonna per filtrare il mese</span>
        </div>
        <ResponsiveContainer width="100%" height={190}>
          <BarChart data={perMese} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f0" />
            <XAxis dataKey="etichetta" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: '#f6faf8' }}
                     formatter={(v) => [`${v} attestati`, '']}
                     labelFormatter={(l) => l === 'scaduti' ? 'Già scaduti' : l} />
            <Bar dataKey="n" radius={[4, 4, 0, 0]} cursor="pointer"
                 onClick={(d) => commuta('mese', d.k)}>
              {perMese.map((m) => (
                <Cell key={m.k}
                      fill={m.scaduti ? ROSSO : (filtri.mese && filtri.mese !== m.k ? GRIGIO : VERDE)}
                      opacity={filtri.mese && filtri.mese !== m.k ? 0.45 : 1} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Su quale corso */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Per corso</h3>
          <span className="text-xs text-gray-400">clicca per filtrare</span>
        </div>
        <div className="space-y-1.5">
          {perCorso.length === 0 && <p className="text-xs text-gray-400 py-4 text-center">Nessun dato.</p>}
          {perCorso.map(c => (
            <div key={c.corso} className="group">
              <button type="button"
                    onClick={() => commuta('corso', c.corso)}
                    className="w-full text-left">
              <div className="flex items-baseline justify-between text-xs">
                <span className={`truncate pr-2 ${attivo('corso', c.corso) ? 'font-semibold text-brand-700' : 'text-gray-700'}`}>
                  {c.corso}
                </span>
                <span className="text-gray-400 shrink-0">{c.tot}</span>
              </div>
              {/* Una barra divisa in tre: quanto è scaduto, quanto sta per
                  scadere, quanto è a posto. Si legge senza leggenda. */}
            </button>
              {/* Ogni segmento è una domanda diversa: il rosso porta agli
                  scaduti DI QUEL corso, l'ambra a quelli che stanno per
                  scadere. Cliccare la barra intera mostra tutto il corso. */}
              <div className="flex h-2.5 rounded overflow-hidden bg-gray-100">
                {[['scaduti', ROSSO, 'scaduti', 'scaduti'],
                  ['entro90', AMBRA, '90', 'in scadenza entro 90 giorni'],
                  ['ok', VERDE, null, 'in regola']].map(([k, col, finestra, che]) => (
                  c[k] > 0 && (
                    <button key={k} type="button"
                            title={`${c[k]} ${che} — ${c.corso}`}
                            onClick={() => onFiltri({ corso: c.corso, finestra, mese: null, persona: null })}
                            style={{ width: `${(c[k] / maxCorso) * 100}%`, background: col }}
                            className="hover:brightness-110 transition" />
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chi convocare */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Chi ha più pendenze</h3>
        <div className="space-y-1">
          {perPersona.length === 0 && (
            <p className="text-xs text-gray-400 py-4 text-center">Nessuno ha scadenze entro 90 giorni.</p>
          )}
          {perPersona.map(p => (
            <div key={p.persona}
                 className={`w-full flex items-center justify-between text-sm px-2 py-1 rounded hover:bg-gray-50
                   ${attivo('persona', p.persona) ? 'bg-brand-50 text-brand-700' : 'text-gray-700'}`}>
              {/* Il nome apre la scheda, il resto della riga filtra: sono due
                  domande diverse — "chi è" e "mostrami solo le sue". */}
              <button type="button" className="truncate pr-2 text-left hover:underline font-medium"
                      onClick={() => onPersona?.(p.persona)}>{p.persona}</button>
              <button type="button" className="flex-1 h-5" aria-label="filtra"
                      onClick={() => commuta('persona', p.persona)} />
              <span className="flex gap-1 shrink-0">
                {p.scaduti > 0 && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{p.scaduti} scaduti</span>
                )}
                {p.imminenti > 0 && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{p.imminenti} in scadenza</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { coloreGiorni };
